import { Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CmxClientService } from '@cemex/cmx-client-layout';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isObservable, Observable, of, Subject } from 'rxjs';
import { pairwise, startWith, take, takeUntil } from 'rxjs/operators';
import { SpecificationFormValues } from '../../../../../order-builder/modules/specification/models/specification-form-values';
import { SpecificationFormEnum } from '../../../../../order-builder/modules/specification/models/specification-form.enum';
import { IAlertMessage } from '../../../../models/alert-message.interface';
import { BaseAsyncValue } from '../../../../models/async-value';
import { FieldConfig } from '../../models/field-configs/field-config.interface';
import { OnFieldChanged } from '../../models/on-field-changed';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { MultipleOrderOnlineState } from '@ordersnproducts/modules/multiple-order-online/redux/reducers';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';

@Component({
  selector: 'configurable-form',
  styleUrls: ['configurable-form.component.scss'],
  templateUrl: 'configurable-form.component.html',
})
export class ConfigurableFormComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public rtl = false;
  @Input() public config: FieldConfig[] = [];
  @Input() public controlChangeListeners: {
    name: string;
    callback: (form: ConfigurableFormComponent, val: any) => void;
  }[] = [];
  @Input() public className = '';

  // Data to prefill
  @Input() public data: SpecificationFormValues = {
    id: 0,
    type: SpecificationFormEnum.PRODUCT_FORM,
    values: {},
  };
  @Input() public mapItem = {}; // Mapped object to the form
  @Input() public enableEmitFormStatusChanges = false;
  @Input() public enableEmitFormValueChanges = false;
  @Output() public submit: EventEmitter<any> = new EventEmitter<any>();
  @Output() public change: EventEmitter<any> = new EventEmitter<any>();
  @Output() public statusChanges: EventEmitter<any> = new EventEmitter<any>();

  public form: UntypedFormGroup;
  private ngDestroy = new Subject();

  get controls(): FieldConfig[] {
    if (this.config !== undefined) {
      return this.config;
    }

    return [];
  }

  get changes(): Observable<any> {
    return this.form ? this.form.valueChanges : of(null);
  }

  get valid(): boolean {
    return this.form ? this.form.valid : false;
  }

  get value(): any | undefined {
    return this.form ? this.form.value : undefined;
  }

  get formStore(): Store<MultipleOrderOnlineState> {
    return this.store;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private injector: Injector,
    private store: Store<MultipleOrderOnlineState>,
    private alerts: CmxClientService,
    private translate: TranslateService,
    private featureToggle: FeatureToggleService,
  ) {}

  ngOnInit() {
    // this initialization has to be before the createGroup
    // otherwise there are wrong control references and it says that control is disabled even when it is not
    this.config = this.config.filter((config) => this.hasFeatureEnabled(config));
    this.config = this.config.filter((config) => this.hasFeatureDisabled(config));
    this.form = this.createGroup();
    if (this.config) {
      this.initConfig();
    }
    if (this.enableEmitFormStatusChanges) {
      this.initFormStatusChanges();
    }
    if (this.enableEmitFormValueChanges) {
      this.initFormValueChanges();
    }
  }

  private resolveConfigContainer(config: FieldConfig): FieldConfig {
    let classNameList = ['dynamic-form-field__' + config.name];
    classNameList = classNameList.concat(config.containerClassNames);
    return { ...config, containerClassNames: classNameList };
  }

  public createGroup(): UntypedFormGroup {
    const group = this.fb.group({});
    _safe(() =>
      this.controls.forEach((control) => {
        const ctrl = this.createControl(control, group);
        group.addControl(control.name, ctrl);
      }),
    );

    return group;
  }

  ngOnChanges(changes) {
    if (this.form) {
      const controls = Object.keys(this.form.controls);
      const configControls = this.controls.map((item) => item.name);

      controls
        .filter((control) => !configControls.includes(control))
        .forEach((control) => this.form.removeControl(control));

      configControls
        .filter((control) => !controls.includes(control))
        .forEach((name) => {
          const config = this.config.find((control) => control.name === name);
          this.form.addControl(name, this.createControl(config, this.form));
        });
    }
  }

  private initConfig() {
    // If the hasFeatureEnabled is false, we need to remove the field from the form in case it has validators
    const filteredFieldNames = this.config.map((config) => config.name);
    Object.keys(this.form.controls).forEach((controlName) => {
      if (!filteredFieldNames.includes(controlName)) {
        this.form.removeControl(controlName);
      }
    });

    this.config = this.config.map((config) => {
      config.fieldInitializer?.(this.form, this.injector, this.ngDestroy);
      if (config.containerClassNames && config.containerClassNames.length) {
        config = this.resolveConfigContainer(config);
      }

      Object.keys(config).forEach((key) => {
        if (this.isAsyncValue(config[key])) {
          // FieldConfig values who need to be resolved inside this component
          const provider = config[key];
          if (['disabled'].includes(key)) {
            config = {
              ...config,
              disabled: new provider(this.store, this.form, this.injector, this.mapItem, this.ngDestroy, config)
                .getValue()
                .pipe(takeUntil(this.ngDestroy)),
            };
          } else if (key === 'hidden') {
            config = {
              ...config,
              hidden: new provider(this.store, this.form, this.injector, this.mapItem, this.ngDestroy, config)
                .getValue()
                .pipe(takeUntil(this.ngDestroy)),
            };
          } else {
            // Values who need to be resovled inside extended component
            config = {
              ...config,
              [key]: new provider(this.store, this.form, this.injector, this.mapItem, this.ngDestroy, config),
            };
          }
        }
      });
      return config;
    });

    for (const key of Object.keys(this.data.values)) {
      this.setValue(key, this.data.values[key]);
    }
  }

  public hasFeatureEnabled(config: FieldConfig) {
    const featureEnabled = config['hasFeatureEnabled'];

    if (!!featureEnabled) {
      return this.featureToggle.isFeatureEnabled(featureEnabled);
    }

    return true;
  }

  public hasFeatureDisabled(config: FieldConfig) {
    const featureDisabled = config['hasFeatureDisabled'];

    if (!!featureDisabled) {
      return !this.featureToggle.isFeatureEnabled(featureDisabled);
    }

    return true;
  }

  public createControl(config: FieldConfig, form: UntypedFormGroup): UntypedFormControl {
    const { validation, value, asyncValidators } = config;

    // Resolve disabled if needed
    let disabled = config.disabled;
    if (this.isAsyncValue(disabled)) {
      new (<any>config.disabled)(this.store, form, this.injector, this.mapItem, this.ngDestroy, config)
        .getValue()
        .pipe(take(1))
        .subscribe((val) => (disabled = val));
    }

    const ctrl = this.fb.control(
      { disabled, value },
      validation,
      asyncValidators &&
        asyncValidators.map((validator) => validator(this.store, this.data, this.injector, this.ngDestroy)),
    );

    return this.registerOnChangeListeners(ctrl, config);
  }

  /**
   * Attaches listener functions to a form control based on its entry in the configuration,
   * using dependency injection.
   * @param {FormControl} ctrl
   * @param {FieldConfig} config
   * @return {FormControl}
   */
  public registerOnChangeListeners(ctrl: UntypedFormControl, config: FieldConfig): UntypedFormControl {
    if (config.onChange === undefined) {
      return ctrl;
    }

    // Assigns listener functions to "ctrl" based on the
    for (const changeListener of config.onChange) {
      const listenerInstance = this.injector.get<OnFieldChanged>(changeListener);
      ctrl.valueChanges
        .pipe(takeUntil(this.ngDestroy), startWith(ctrl.value), pairwise())
        .subscribe(([prev, changedValue]) => {
          listenerInstance.onFieldChanged(changedValue, this.form, this, config, prev);
        });
    }

    return ctrl;
  }

  private isAsyncValue(field) {
    try {
      return new field() instanceof BaseAsyncValue;
    } catch {
      return false;
    }
  }

  public handleSubmit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.submit.emit(this.value);
  }

  public handleChange(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.change.emit(event);
  }

  public setDisabled(name: string, disable: boolean) {
    if (this.form.controls[name]) {
      const method = disable ? 'disable' : 'enable';
      this.form.controls[name][method]();
      return;
    }

    this.config = this.config.map((item) => {
      if (item.name === name) {
        item.disabled = disable;
      }
      return item;
    });
  }

  public setHidden(name: string, hidden: boolean) {
    this.config = this.config.map((item) => {
      if (item.name === name) {
        return {
          ...item,
          hidden: hidden,
        } as FieldConfig;
      }
      return item;
    });
  }

  /**
   * Updates the form control configuration by name.
   * Triggers the onChange config property to ensure change listeners are executed.
   * @param {string} name - The name of the form control to update.
   */
  public updateValues(name: string): void {
    this.config = this.config.map((item) => (item.name === name ? { ...item } : item));

    const control = this.form.controls[name];
    if (control) {
      control.updateValueAndValidity();
    }
  }

  /**
   * Marks all form-controls as 'touched', as if the user has tabbed through them
   * without changing the value.  The effect of this is to let errors be shown in
   * the form.
   */
  public updateValueAndValidity(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key).markAsTouched({ onlySelf: true });
      if (this.form.get(key).invalid) {
        const alertMessage: IAlertMessage = this.form.get(key).errors.alertMessage;
        alertMessage && this.alerts.addMainAlert(alertMessage.type, this.translate.instant(alertMessage.text));
      }
    });
  }

  public setValue(name: string, value: any) {
    if (this.form.controls[name] === undefined || this.form.controls[name].value === null) {
      return;
    }

    this.form.controls[name].setValue(value, { emitEvent: true });
  }

  public resolveHidden(hidden: boolean | Observable<boolean>): Observable<boolean> {
    if (typeof hidden === 'boolean') {
      return of(hidden);
    } else if (isObservable(hidden)) {
      return hidden;
    } else {
      return of(false);
    }
  }

  /**
   * Initializes the form status changes subscription.
   * This method subscribes to the statusChanges observable of the form and emits the status changes
   * using the statusChanges event emitter.
   */
  public initFormStatusChanges() {
    this.form.statusChanges.pipe(takeUntil(this.ngDestroy)).subscribe((status) => {
      this.statusChanges.emit(status);
    });
  }

  /**
   * Initializes the form value changes subscription.
   * This method subscribes to the valueChanges observable of the form and emits the value changes
   * using the change event emitter.
   */
  private initFormValueChanges() {
    this.form.valueChanges.pipe(takeUntil(this.ngDestroy)).subscribe((value) => {
      this.change.emit(value);
      this.executeFormConfigChangeHandlers();
    });
  }

  private executeFormConfigChangeHandlers(): void {
    this.config.forEach((config) => {
      if (config.onFormChange) {
        config.onFormChange.forEach((change) => {
          change(this.store, this.data, this.form);
        });
      }
    });
  }

  public ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }
}
